import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type ActionResult } from '@/lib/api'
import { centeredSwal, closeAlert, showLoading } from '@/lib/alert'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useAuth } from '@/auth/AuthContext'
import { PasswordInput } from '@/components/PasswordInput'
import { Recaptcha, type RecaptchaHandle } from '@/components/Recaptcha'
import { LegalModals } from '@/features/account/LegalModals'
import { sendOtp } from '@/features/account/otp'

/**
 * Port of Views/Account/Login.cshtml + wwwroot/js/app/account/login.js.
 *
 * The submit sequence is preserved step for step: HTML5 validity ->
 * `was-validated` -> reCAPTCHA presence -> POST -> branch on
 * `success` / `requiresVerification`. The dialogs keep their original titles and
 * timings ("Login Berhasil!", 2s auto-close; "Account Not Verified" with a
 * Verify Now button).
 *
 * Two things from the original are gone by design:
 *  - The `RememberMe` checkbox is kept because it was on screen, but it is inert
 *    and always was: `RememberMe` appears nowhere in the C#. vmLoginCandidates
 *    binds only Email, Password and RecaptchaToken, so the value was posted and
 *    dropped. Wiring it up now would be a new feature, not a port.
 *  - login.js repeated every dialog three times over
 *    `typeof Swal !== 'undefined'`, `window.Swal` and `alert()` fallbacks.
 *    SweetAlert2 is a bundled import here, so it cannot be missing.
 */
export function Login(): JSX.Element {
  usePageTitle('Login', 'auth')
  const navigate = useNavigate()
  const { refresh } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [wasValidated, setWasValidated] = useState(false)

  const formRef = useRef<HTMLFormElement>(null)
  const recaptchaRef = useRef<RecaptchaHandle>(null)

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault()

    const form = formRef.current
    if (!form) return

    setWasValidated(true)
    if (!form.checkValidity()) return

    const recaptchaToken = recaptchaRef.current?.getResponse() ?? ''
    if (recaptchaToken.length === 0) {
      await centeredSwal({
        icon: 'warning',
        title: 'Verifikasi',
        text: 'Please verify that you are not a robot.',
        confirmButtonColor: '#3b82f6',
      })
      return
    }

    setIsSubmitting(true)
    try {
      const data = await api.post<ActionResult>('/api/ecareer/account/login', {
        Email: email,
        Password: password,
        RecaptchaToken: recaptchaToken,
      })

      if (data.success) {
        await refresh()
        await centeredSwal({
          icon: 'success',
          title: 'Login Berhasil!',
          text: 'Selamat datang kembali',
          timer: 2000,
          showConfirmButton: false,
          timerProgressBar: true,
        })
        // A full load rather than navigate(): the session cookie has just
        // changed, and this guarantees no query cached while signed out
        // survives into the signed-in view.
        window.location.assign(data.redirectUrl ?? '/')
        return
      }

      // A failed attempt burns the reCAPTCHA token; without a reset the widget
      // stays ticked but returns '' and the next submit is rejected for a
      // reason the user cannot see.
      recaptchaRef.current?.reset()

      if (data.requiresVerification) {
        const result = await centeredSwal({
          icon: 'warning',
          title: 'Account Not Verified',
          text: 'Your account needs to be verified before you can login.',
          showCancelButton: true,
          confirmButtonText: 'Verify Now',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
        })
        if (result.isConfirmed) await sendVerificationOtp()
        return
      }

      await centeredSwal({
        icon: 'error',
        title: 'Login Gagal',
        text: data.message ?? 'Login failed. Please try again.',
        confirmButtonText: 'OK',
      })
    } catch (error) {
      recaptchaRef.current?.reset()
      await centeredSwal({
        icon: 'error',
        title: 'Error',
        text: error instanceof Error ? error.message : 'Login failed. Please try again.',
        confirmButtonText: 'OK',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Login.sendOTPLogin. On failure it still sends the user to the verification
   * page — they can resend from there — which is what the original's 3s
   * setTimeout did.
   */
  async function sendVerificationOtp(): Promise<void> {
    showLoading('Sending verification email...')
    try {
      const data = await sendOtp(email, 'register')
      closeAlert()

      if (data.success) {
        await centeredSwal({
          icon: 'success',
          title: 'Success',
          text: 'Verification email sent! Please check your email for the verification code.',
          timer: 3000,
          showConfirmButton: false,
          timerProgressBar: true,
        })
        navigate(data.redirectUrl ?? '/Account/ConfirmEmailRegist')
        return
      }

      await centeredSwal({
        icon: 'error',
        title: 'Error',
        text: data.message ?? 'Failed to send verification email. Please try again.',
        confirmButtonText: 'OK',
      })
    } catch {
      closeAlert()
      await centeredSwal({
        icon: 'error',
        title: 'Error',
        text: 'Failed to send verification email. Please try to resend from the verification page.',
        confirmButtonText: 'OK',
      })
      navigate('/Account/ConfirmEmailRegist')
    }
  }

  return (
    <>
      <div className="text-center">
        <img
          src="/lib/images/authentication/img-auth-login.png"
          alt="Login"
          className="img-fluid mb-3"
        />
        <h4 className="f-w-500 mb-1">Login with your email</h4>
        <p className="mb-3">
          Don&apos;t have an Account?
          <Link to="/Account/Register" className="link-primary ms-1">
            Create Account
          </Link>
        </p>
      </div>

      <form
        ref={formRef}
        id="loginForm"
        onSubmit={(event) => void handleSubmit(event)}
        noValidate
        className={wasValidated ? 'was-validated' : undefined}
      >
        <div className="mb-3">
          <input
            type="email"
            className="form-control"
            id="Email"
            name="Email"
            placeholder="Email Address"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
          />
        </div>

        <div className="mb-3">
          <PasswordInput
            id="Password"
            value={password}
            onChange={setPassword}
            placeholder="Password"
            required
            autoComplete="current-password"
          />
        </div>

        <div className="d-flex mt-1 justify-content-between align-items-center">
          <div className="form-check">
            <input
              className="form-check-input input-primary"
              type="checkbox"
              name="RememberMe"
              id="RememberMe"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
            />
            <label className="form-check-label text-muted" htmlFor="RememberMe">
              Remember me?
            </label>
          </div>
          <Link to="/Account/ForgotPassword">
            <h6 className="f-w-400 mb-0">Forgot Password?</h6>
          </Link>
        </div>

        <div className="d-flex justify-content-center my-3">
          <Recaptcha ref={recaptchaRef} />
        </div>

        <div className="d-grid mt-4">
          <button type="submit" className="btn btn-primary" id="loginBtn" disabled={isSubmitting}>
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        </div>

        <p className="text-muted text-center mt-3 mb-0" style={{ fontSize: 12 }}>
          By logging in, you agree to our{' '}
          <a href="#" className="link-primary" data-bs-toggle="modal" data-bs-target="#modalToS">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="#" className="link-primary" data-bs-toggle="modal" data-bs-target="#modalPrivacy">
            Privacy Policy
          </a>
        </p>
      </form>

      <LegalModals />
    </>
  )
}
